import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import ThinSearch from 'components/shared/ThinSearch';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall } from 'styles/typography';

// The "View N more" modal opened from a payer card: the full list of a payer's alternate names with
// a client-side substring search. Portaled to <body> so it escapes the slideout's transform/stacking
// context — Foldout's panel uses translateX, which would otherwise clip a position:fixed modal.

interface Props {
  payerName: string;
  names: string[];
  onClose: () => void;
}

export default function PayerOtherNamesModal({ payerName, names, onClose }: Props) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? names.filter((n) => n.toLowerCase().includes(q)) : names;
  }, [names, query]);

  return createPortal(
    <StandardModal title={payerName} onCancel={onClose} showCloseButton closeOnBackdropClick maxWidth='520px'>
      <Count data-testid='other-names-count'>{names.length} Other Names</Count>
      <ThinSearch value={query} onChange={setQuery} placeholder='Search' />
      <List data-testid='other-names-list'>
        {filtered.length === 0 ? (
          <Empty>No names match “{query}”.</Empty>
        ) : (
          filtered.map((name) => <Name key={name}>{name}</Name>)
        )}
      </List>
      <Footer>
        <Button variant='primary' onClick={onClose}>
          Close
        </Button>
      </Footer>
    </StandardModal>,
    document.body
  );
}

const Count = styled(Body)`
  text-align: center;
  font-weight: 600;
  color: ${colors.black100};
  margin-bottom: 16px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 360px;
  overflow-y: auto;
  margin-top: 16px;
`;

const Name = styled(BodySmall)`
  padding: 12px 4px;
  color: ${colors.black100};
  border-bottom: 1px solid ${colors.black10};
`;

const Empty = styled(BodySmall)`
  padding: 24px 4px;
  text-align: center;
  color: ${colors.black50};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
